/**
 * Inline brand-logo SVGs for the three Playwright browser engines.
 * Used by `BrowserSelect` so the operator can scan the radio row and
 * recognise each option by its brand mark, not just its label.
 *
 * Why hand-rolled inline SVG vs an icon dep:
 *
 *   - lucide-react (the codebase's icon library) ships a Chrome icon
 *     that is monochrome and brand-inaccurate, no Firefox or Safari
 *     icons that read as the real product. Using `Flame` / `Globe`
 *     stand-ins (the pre-#994 state) gave the operator nothing to
 *     latch onto.
 *   - simple-icons / react-icons would solve the recognition problem
 *     but pulls in a multi-kilobyte registry for three glyphs. Three
 *     hand-rolled SVGs are smaller, render once, and don't bind us to
 *     a vendor's release cadence.
 *
 * Each component accepts the standard `width` / `height` / `className`
 * props the lucide icons did, so the call sites don't have to change
 * shape — the swap in `BrowserSelect` is one-for-one.
 *
 * Colors are intentionally hard-coded in the SVG (not theme tokens):
 * brand colors are the point. The radio's selected/unselected state
 * is conveyed by the SURROUNDING label's border + bg (per
 * `BrowserSelect`'s existing pattern); the icon itself stays
 * full-color in both states so recognition doesn't degrade when
 * unselected.
 */
import type { SVGProps } from 'react'

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox' | 'xmlns'>

/** Chromium — the multi-color disc Google ships as the Chrome logo.
 * Chromium-the-engine has its own (blue-only) mark, but the operator-
 * recognition test we're optimizing for is "would a non-technical
 * tester know what they're picking?" — and that answer is reliably
 * "the Chrome logo." Using the recognisable mark beats brand purism. */
export function ChromiumBrandIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      // Decorative — the surrounding radio label + caption carry
      // the accessible name. Without aria-hidden / focusable=false
      // screen readers announce the SVG as an unlabeled graphic
      // and the focus tree picks it up on tab traversal (PR #1030
      // Copilot review).
      aria-hidden="true"
      focusable={false}
      {...props}
    >
      {/* Three brand-colored arcs around a blue center disc. The arcs
          are stroke-only paths sized to clear the center disc; the
          center disc is on top to mask the inner edges. */}
      <circle cx="24" cy="24" r="22" fill="#fff" />
      <path d="M24 2 a22 22 0 0 1 19.05 11 L24 13 a11 11 0 0 0 -9.526 5.5 z" fill="#ea4335" />
      <path d="M43.05 13 A22 22 0 0 1 24 46 L33.526 29.5 a11 11 0 0 0 0 -11 z" fill="#fbbc05" />
      <path d="M24 46 A22 22 0 0 1 4.95 13 L14.474 29.5 a11 11 0 0 0 9.526 5.5 z" fill="#34a853" />
      <circle cx="24" cy="24" r="9" fill="#1a73e8" />
      <circle cx="24" cy="24" r="7" fill="#fff" />
      <circle cx="24" cy="24" r="6" fill="#1a73e8" />
    </svg>
  )
}

/** Firefox — the orange flame-fox curling around a blue Earth.
 * Simplified: the real Mozilla mark has gradient stops that don't
 * carry at 16px; we use solid orange + a small blue planet behind it
 * which reads correctly at the icon sizes used in the radio row
 * (16–24px). */
export function FirefoxBrandIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      // Decorative — the surrounding radio label + caption carry
      // the accessible name. Without aria-hidden / focusable=false
      // screen readers announce the SVG as an unlabeled graphic
      // and the focus tree picks it up on tab traversal (PR #1030
      // Copilot review).
      aria-hidden="true"
      focusable={false}
      {...props}
    >
      {/* Earth (blue, behind). */}
      <circle cx="24" cy="24" r="18" fill="#1d65b8" />
      {/* Fox body (orange, in front) — three overlapping rounded
          shapes that read as a curling tail-and-head silhouette. */}
      <path
        d="M24 6 C 14 6 6 14 6 24 C 6 34 14 42 24 42 C 30 42 36 38 38 32 C 32 36 26 34 22 30 C 18 26 18 20 22 16 C 26 12 32 12 36 14 C 34 9 30 6 24 6 z"
        fill="#ff7139"
      />
      <path
        d="M30 14 C 26 14 22 18 22 24 C 22 28 26 32 30 32 C 36 32 40 28 40 22 C 40 17 35 14 30 14 z"
        fill="#ffb33b"
      />
    </svg>
  )
}

/** WebKit / Safari — a blue compass face with a red-and-white needle.
 * Safari's brand mark is what operators recognise (WebKit-the-engine
 * has only a logo-mark used in developer docs, not in any UI a user
 * has encountered). Picking the compass aligns operator recognition
 * with the radio label "WebKit", which lists "Safari (desktop + iOS)"
 * as its proxy targets. */
export function WebKitBrandIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      // Decorative — the surrounding radio label + caption carry
      // the accessible name. Without aria-hidden / focusable=false
      // screen readers announce the SVG as an unlabeled graphic
      // and the focus tree picks it up on tab traversal (PR #1030
      // Copilot review).
      aria-hidden="true"
      focusable={false}
      {...props}
    >
      {/* Outer disc. */}
      <circle cx="24" cy="24" r="22" fill="#1e90ff" />
      <circle cx="24" cy="24" r="18" fill="#f4f7fb" />
      {/* Tick marks at N / E / S / W. */}
      <rect x="23" y="6" width="2" height="4" fill="#1e90ff" />
      <rect x="23" y="38" width="2" height="4" fill="#1e90ff" />
      <rect x="6" y="23" width="4" height="2" fill="#1e90ff" />
      <rect x="38" y="23" width="4" height="2" fill="#1e90ff" />
      {/* Two-tone needle pointing NE — red half (active) + white
          half (counter). The diamond shape gives the unmistakable
          compass-needle silhouette at small sizes. */}
      <polygon points="24,12 28,24 24,36 20,24" fill="#fff" stroke="#1e90ff" strokeWidth="1" />
      <polygon points="24,12 28,24 24,24" fill="#ff3b30" />
      {/* Center hub. */}
      <circle cx="24" cy="24" r="2" fill="#1e90ff" />
    </svg>
  )
}
